import { useFieldRef } from "@/components/calculator/fields/useFieldRef";
import { AlertCircle, Check } from "lucide-react";
import { isYes } from "@/lib/paths";
import { HelperTooltip } from "@/components/calculator/fields/HelperTooltip";

/** Two large tappable cards instead of radio dots — far kinder on mobile,
 *  and it stops the flow feeling like a tax return. */
export const YesNoField = ({ field, value, onChange, error, autoFocus = false, registerRef }) => {
  const wrapRef = useFieldRef({
    path: field.path,
    registerRef,
    autoFocus,
    selector: "button",
  });
  const answered = value !== null && value !== undefined && value !== "";
  const yes = answered && isYes(value);
  const no = answered && !isYes(value);

  const base =
    "group relative flex min-h-[58px] flex-1 items-center gap-3 rounded-lg border px-4 py-3 text-left outline-none transition-[border-color,background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-primary/30";
  const selected =
    "border-primary bg-[color:var(--tt-teal-mist)] shadow-[inset_0_0_0_1px_var(--tt-teal)]";
  const idle = "border-input bg-white hover:border-[color:var(--tt-teal-line)] hover:bg-muted";

  return (
    <div
      ref={wrapRef}
      data-testid={`yesno-field-${field.path}`}
      className="border-b border-border pb-5 last:border-b-0 last:pb-0"
    >
      <div className="flex items-start gap-2">
        <p className="text-[15px] font-semibold leading-snug text-foreground">{field.label}</p>
        <HelperTooltip text={field.tooltip} />
      </div>
      {field.help ? (
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{field.help}</p>
      ) : null}

      <div
        className="mt-3 flex gap-3"
        role="radiogroup"
        aria-label={field.label}
        aria-invalid={error ? "true" : "false"}
      >
        <button
          type="button"
          role="radio"
          aria-checked={yes}
          data-testid={`yesno-yes-${field.path}`}
          onClick={() => onChange(true)}
          className={`${base} ${yes ? selected : idle}`}
        >
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150 ${
              yes ? "border-primary bg-primary text-white" : "border-input bg-white"
            }`}
            aria-hidden="true"
          >
            {yes ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
          </span>
          <span>
            <span
              className={`block text-sm font-semibold ${
                yes ? "text-[color:var(--tt-teal-deep)]" : "text-foreground"
              }`}
            >
              {field.yesLabel || "Yes"}
            </span>
            {field.yesHint ? (
              <span className="mt-0.5 block text-xs text-muted-foreground">{field.yesHint}</span>
            ) : null}
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={no}
          data-testid={`yesno-no-${field.path}`}
          onClick={() => onChange(false)}
          className={`${base} ${no ? selected : idle}`}
        >
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150 ${
              no ? "border-primary bg-primary text-white" : "border-input bg-white"
            }`}
            aria-hidden="true"
          >
            {no ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
          </span>
          <span>
            <span
              className={`block text-sm font-semibold ${
                no ? "text-[color:var(--tt-teal-deep)]" : "text-foreground"
              }`}
            >
              {field.noLabel || "No"}
            </span>
            {field.noHint ? (
              <span className="mt-0.5 block text-xs text-muted-foreground">{field.noHint}</span>
            ) : null}
          </span>
        </button>
      </div>

      {error ? (
        <p
          data-testid={`field-error-${field.path}`}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
};
